import React, { useEffect, useState } from 'react';
import { Image as ImageIcon, Lock } from 'lucide-react';
import { getAgentGallery, type GalleryPost } from '../../services/galleryService';
import { useStorageUrl } from '../chatHome/MemberAvatar';

/** One gallery thumbnail — resolves its storage path to a signed URL (needs auth). */
const Thumb: React.FC<{ post: GalleryPost }> = ({ post }) => {
  const url = useStorageUrl(post.image_path);
  return (
    <div className="aspect-square rounded-lg overflow-hidden bg-secondary-100 dark:bg-secondary-800">
      {post.image_path && url ? <img src={url} alt={post.caption || ''} className="w-full h-full object-cover" loading="lazy" /> : null}
    </div>
  );
};

/**
 * A login-gated preview of an agent's social-media gallery on the public /agent/{slug} page. Logged out → a
 * teaser that prompts login; logged in → up to four thumbnails + a link into the chat where the full gallery
 * lives. Gallery images are auth-only (the route + storage need a signed-in user), so the teaser gates access.
 */
const AgentGalleryTeaser: React.FC<{ agentId: string; agentName: string; authed: boolean; onOpen: () => void }> = ({ agentId, agentName, authed, onOpen }) => {
  const [posts, setPosts] = useState<GalleryPost[] | null>(null);
  useEffect(() => {
    if (!authed) return;
    let alive = true;
    getAgentGallery(agentId).then((v) => { if (alive) setPosts(v.posts || []); }).catch(() => { if (alive) setPosts([]); });
    return () => { alive = false; };
  }, [agentId, authed]);

  if (!authed) {
    return (
      <section className="mt-6 rounded-2xl border border-secondary-200 dark:border-secondary-700 p-5">
        <div className="flex items-center gap-2 text-secondary-600 dark:text-secondary-300"><ImageIcon className="w-4 h-4" /><span className="text-sm font-semibold">{agentName}&apos;s social gallery</span></div>
        <p className="mt-1 text-sm text-secondary-500">Log in to preview {agentName}&apos;s posts.</p>
        <button onClick={onOpen} className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-secondary-300 dark:border-secondary-700 px-4 py-2 text-sm font-medium hover:bg-secondary-100 dark:hover:bg-secondary-800">
          <Lock className="w-4 h-4" /> Log in to view
        </button>
      </section>
    );
  }

  const shown = (posts || []).filter((p) => p.image_path).slice(0, 4);
  if (posts !== null && shown.length === 0) return null; // nothing to preview yet

  return (
    <section className="mt-6">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-secondary-400">Social gallery</h2>
      <div className="mt-3 grid grid-cols-4 gap-2">
        {posts === null
          ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-square rounded-lg bg-secondary-100 dark:bg-secondary-800 animate-pulse" />)
          : shown.map((p) => <Thumb key={p.id} post={p} />)}
      </div>
    </section>
  );
};

export default AgentGalleryTeaser;
